import React, { useState, useEffect, useCallback } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import ReplayIcon from '@material-ui/icons/Replay'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import TranslateIcon from '@material-ui/icons/Translate'
import { FormattedMessage } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import renderFormattedText from './utils/renderFormattedText'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

const ScenarioAudioPlayType = {
    DontPlay: 0,
    PlayBefore: 1,
    PlayAfter: 2,
}

const InteractiveElementType = {
    Image: 7,
    Instruction: 13,
    MentorAnimation: 15,
}

const getElementType = (to) =>
    to.scenarioInteractiveElementType ?? to.interactiveElementType ?? 0

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: '24px 16px',
        boxSizing: 'border-box',
        '@media (max-width: 599px)': {
            padding: '12px 8px',
        },
    },
    cardArea: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        gap: 20,
        minHeight: 0,
        overflowY: 'auto',
        '@media (max-width: 599px)': {
            gap: 14,
        },
    },
    card: {
        width: '100%',
        maxWidth: 400,
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 500,
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: 600,
        },
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            borderRadius: 14,
        },
    },
    image: {
        display: 'block',
        maxWidth: 360,
        width: '100%',
        borderRadius: 12,
        objectFit: 'contain',
        '@media (max-width: 599px)': {
            maxWidth: '100%',
            maxHeight: '30vh',
            objectFit: 'contain',
        },
    },
    contentSection: {
        backgroundColor: '#FFFFFF',
        padding: '20px 24px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        [theme.breakpoints.up('sm')]: {
            padding: '24px 32px 20px',
        },
    },
    instructionText: {
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '170%',
        color: CapeeshColors.DarkText,
        whiteSpace: 'pre-wrap',
        [theme.breakpoints.up('sm')]: {
            fontSize: 18,
        },
    },
    translationText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: '160%',
        color: 'rgba(0, 0, 0, 0.55)',
        whiteSpace: 'pre-wrap',
        fontStyle: 'italic',
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        paddingTop: 12,
        [theme.breakpoints.up('sm')]: {
            fontSize: 15,
        },
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 12px',
        backgroundColor: CapeeshColors.PurpleBright,
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
    toolbarIcon: {
        color: 'rgba(255, 255, 255, 0.85)',
        '&:hover': {
            color: '#FFFFFF',
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
    },
    toolbarIconPlaying: {
        color: '#FFFFFF',
        animation: '$pulse 1.5s ease-in-out infinite',
    },
    '@keyframes pulse': {
        '0%, 100%': { transform: 'scale(1)', opacity: 1 },
        '50%': { transform: 'scale(1.15)', opacity: 0.7 },
    },
    translateIcon: {
        color: 'rgba(255, 255, 255, 0.85)',
        '&:hover': {
            color: '#FFFFFF',
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
    },
    translateIconActive: {
        color: '#FFFFFF',
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
        },
    },
    bottomArea: {
        display: 'flex',
        justifyContent: 'center',
        flexShrink: 0,
        width: '100%',
        padding: '20px 0 20px',
        boxSizing: 'border-box',
        '@media (max-width: 599px)': {
            padding: '12px 16px 20px',
        },
    },
    continueButton: {
        padding: '16px 48px',
        borderRadius: 28,
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: CapeeshColors.Green,
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        width: '100%',
        maxWidth: 400,
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.2)',
        },
        [theme.breakpoints.up('sm')]: {
            maxWidth: 500,
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: 600,
        },
    },
}))

const InstructionNode = ({ node, imageMap, audioMap, onNodeComplete, isNativeSpeaker = false }) => {
    const classes = useStyles()
    const audioPlayer = useAudioPlayer()
    const [showTranslation, setShowTranslation] = useState(false)

    const testOptions = node.testOptions || []

    const instructionOption = testOptions.find(
        (to) => getElementType(to) === InteractiveElementType.Instruction
    )
    const imageOption = testOptions.find(
        (to) => getElementType(to) === InteractiveElementType.Image
    )

    const instructionText = instructionOption?.text || ''
    const explanationText = instructionOption?.explanation || ''
    const audioPlayType = instructionOption?.audioPlayType ?? ScenarioAudioPlayType.DontPlay
    const audioUrl = instructionOption?.textAudioUrl
        ? audioMap[instructionOption.textAudioUrl]
        : null

    const imageUrl = imageOption?.imageUrl
        ? imageMap[imageOption.imageUrl]
        : null

    useEffect(() => {
        if (audioPlayType === ScenarioAudioPlayType.PlayBefore && audioUrl) {
            audioPlayer.playAudio(audioUrl)
        }
        return () => audioPlayer.stopAudio()
    }, [])

    const handleReplayAudio = useCallback(() => {
        if (audioUrl) {
            audioPlayer.stopAudio()
            audioPlayer.playAudio(audioUrl)
        }
    }, [audioUrl, audioPlayer])

    const handleContinue = useCallback(() => {
        audioPlayer.stopAudio()
        onNodeComplete({
            nodeId: node.nodeId,
            chosenAlternativeIndex: 0,
        })
    }, [node.nodeId, onNodeComplete, audioPlayer])

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Enter') handleContinue()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [handleContinue])

    const hasAudio = !!audioUrl
    const hasTranslation = !isNativeSpeaker && !!explanationText

    return (
        <div className={classes.container}>
            <div className={classes.cardArea}>
                {imageUrl && (
                    <img src={imageUrl} alt="" className={classes.image} />
                )}

                <div className={classes.card}>
                    <div className={classes.contentSection}>
                        <Typography className={classes.instructionText}>
                            {renderFormattedText(instructionText)}
                        </Typography>

                        {showTranslation && hasTranslation && (
                            <Typography className={classes.translationText}>
                                {renderFormattedText(explanationText)}
                            </Typography>
                        )}
                    </div>

                    {(hasAudio || hasTranslation) && (
                        <div className={classes.toolbar}>
                            <div className={classes.toolbarLeft}>
                                {hasAudio && (
                                    <>
                                        <IconButton
                                            className={classes.toolbarIcon}
                                            onClick={handleReplayAudio}
                                            size="small"
                                        >
                                            <ReplayIcon />
                                        </IconButton>
                                        <IconButton
                                            className={
                                                audioPlayer.isPlaying
                                                    ? classes.toolbarIconPlaying
                                                    : classes.toolbarIcon
                                            }
                                            onClick={handleReplayAudio}
                                            size="small"
                                        >
                                            <VolumeUpIcon />
                                        </IconButton>
                                    </>
                                )}
                            </div>
                            {hasTranslation && (
                                <IconButton
                                    className={
                                        showTranslation
                                            ? classes.translateIconActive
                                            : classes.translateIcon
                                    }
                                    onClick={() => setShowTranslation((prev) => !prev)}
                                    size="small"
                                >
                                    <TranslateIcon />
                                </IconButton>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className={classes.bottomArea}>
                <button className={classes.continueButton} onClick={handleContinue}>
                    <FormattedMessage {...playerMessages.continueButton} />
                </button>
            </div>
        </div>
    )
}

export default InstructionNode
